import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
  output,
  viewChild,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';

import { DatasetColumn } from '@blaze-shared/schema-domain';

import { CellDateComponent } from '../date/cell-date.component';
import { CellTimeComponent } from '../time/cell-time.component';

@UntilDestroy()
@Component({
  selector: 'blaze-cell-date-time',
  templateUrl: './cell-date-time.component.html',
  styleUrls: ['./cell-date-time.component.scss', '../../base-cell-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CellDateComponent, CellTimeComponent],
})
export class CellDateTimeComponent implements AfterViewInit {
  column = input.required<DatasetColumn>();
  rowNum = input.required<number>();
  value = input<unknown>();
  isDisabled = input<boolean>(false);

  valueChanged = output<string | null>();

  cellDateComponent = viewChild<CellDateComponent>('cellDateComponent');
  cellTimeComponent = viewChild<CellTimeComponent>('cellTimeComponent');

  protected valueTransformed = linkedSignal<{ date: string | null; time: string | null }>(() => {
    const value = this.value();
    this.assertValueType(value);
    if (value === null || value === undefined || !value) {
      return { date: null, time: null };
    }

    const dateParts = value.split('T');
    return {
      date: dateParts[0],
      time: dateParts[1],
    };
  });

  protected linkedValue = linkedSignal<string | null>(() => {
    if (typeof this.value() === 'string') {
      return this.value() as string;
    } else if (this.value() === null || this.value() === undefined) {
      return null;
    }
    throw new TypeError(
      `CellDateTimeComponent: value is expected to be string | null, got ${typeof this.value()}`,
    );
  });

  protected dateValue = computed<string | null>(() => {
    const valueParts = this.valueTransformed();
    const value = valueParts.date;
    if (value === null || value === undefined || !value) {
      return null;
    }

    let dateString = '';
    if (valueParts.time) {
      const date = new Date(value + 'T' + valueParts.time || '00:00:00');
      const dateDate = date.getDate().toString().padStart(2, '0');
      const dateMonth = (date.getMonth() + 1).toString().padStart(2, '0');
      const dateYear = date.getFullYear().toString();
      dateString = `${dateYear}-${dateMonth}-${dateDate}`;
    } else {
      dateString = value;
    }

    return dateString;
  });

  protected timeValue = computed<string | null>(() => {
    const valueParts = this.valueTransformed();
    const value = valueParts.time;
    if (value === null || value === undefined || !value) {
      return null;
    }

    let timeString = '';
    if (valueParts.date) {
      const date = new Date(valueParts.date + 'T' + value);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      timeString = `${hours}:${minutes}`;
    } else {
      timeString = value.slice(0, 5);
    }

    return timeString;
  });

  ngAfterViewInit(): void {
    this.cellDateComponent()?.openDatePicker();
  }

  protected datePickerOpened(): void {
    this.cellTimeComponent()?.closeTimePicker();
  }

  protected datePickerClosed(): void {
    this.cellTimeComponent()?.openTimePicker();
  }

  protected timePickerOpened(): void {
    this.cellDateComponent()?.closeDatePicker();
  }

  protected dateUpdated(value: string | null): void {
    const oldValue = this.linkedValue();
    if (!value) {
      this.emitValueChanged(null);
      return;
    }
    const newDate = oldValue ? new Date(oldValue) : new Date(0, 0, 0, 0, 0, 0, 0);
    const dateParts = value.split('-').map(Number);
    newDate.setFullYear(dateParts[0], dateParts[1] - 1, dateParts[2]);

    this.linkedValue.set(newDate.toISOString());

    this.emitValueChanged(newDate.toISOString());
  }

  protected timeUpdated(value: string | null): void {
    const oldValue = this.linkedValue();
    if (!value) {
      this.emitValueChanged(null);
      return;
    }
    const newTime = oldValue ? new Date(oldValue) : new Date(0, 0, 0, 0, 0, 0, 0);
    const timeParts = value.split(':').map(Number);
    newTime.setHours(timeParts[0], timeParts[1]);

    this.linkedValue.set(newTime.toISOString());

    this.emitValueChanged(newTime.toISOString());
  }

  private emitValueChanged(value: string | null): void {
    this.valueChanged.emit(value);
  }

  private assertValueType(value: unknown): asserts value is string | null {
    if (typeof value !== 'string' && value !== null) {
      throw new TypeError(
        `CellDateTimeComponent: value is expected to be string | null, got ${typeof value}`,
      );
    }
  }
}
